import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Grid, Skeleton } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendContext } from "contexts/QuickSend";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { VaSettingsContext } from "contexts/VaSettings";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CustomEvent } from "schemas/functions";
import { routes } from "schemas/routes";

import StyledDialog from "components/Styled/Dialog";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import { containsMergeTagsTemplates } from "utils/string";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy, updateList } from "utils/updateLocalState";
import { useBulkSend } from "utils/useBulkSend";

import TemplateBody from "../../SharedComponents/TemplateBody";
import { validateTemplateBody } from "../../SharedComponents/helpers";
import PreSaveDialog from "./components/PreSaveDialog";
import UnsavedChanges from "./components/UnsavedChanges";
import UserFirstTemplate from "./components/UserFirstTemplate";
import styles from "./styles";

interface EditTemplateDialogProps {
  isNew?: boolean;
  propsTemplateId?: string;
  propsHandleClose?: (e?: CustomEvent) => void;
  onSuccess?: () => void;
}

export default function EditTemplateDialog({
  isNew = false,
  propsTemplateId,
  propsHandleClose,
  onSuccess,
}: EditTemplateDialogProps) {
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  let { templateId } = useParams();
  if (propsTemplateId) {
    templateId = propsTemplateId;
    isNew = false;
  }
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { currentOrg, setProfile } = useContext(OrganizationUserContext);
  const { quickSendBrandId, setPreviewLoading, isEdited } = useContext(
    QuickSendDrawerContext,
  );
  const { fetchSettings, loadData } = useContext(VaSettingsContext);
  const { fetchNiches } = useBulkSend();
  const {
    templates,
    setTemplates,
    fetchOutreachTemplates,
    userHasNoTemplates,
    setDefaultTemplates,
    setUserHasNoTemplates,
    currentTemplateContentType,
    associatedNicheId,
    setAssociatedNicheId,
  } = useContext(OutreachTemplatesContext);
  const {
    setTemplateForQuickSend,
    newlyAddedTemplateIndex,
    handleRefetchPreviews,
    setOutreachMessages,
  } = useContext(QuickSendContext);

  const getDefaultContentType = () => {
    if (templates?.length === 0) return TemplateContentType.OUTREACH;
    return (
      currentTemplateContentType ||
      (newlyAddedTemplateIndex > 0
        ? TemplateContentType.FOLLOWUP
        : TemplateContentType.OUTREACH)
    );
  };
  const isUserFirstTemplate = templates?.filter((x) => x.id!)?.length === 0;

  // States
  const [editTemplate, setEditTemplate] = useState<Template>({
    name: isUserFirstTemplate ? "My Outreach" : "",
    subject: "",
    body: "",
    contentType: getDefaultContentType(),
    attachment: undefined,
  });
  const [followupTemplate, setFollowupTemplate] = useState<Template>({
    name: isUserFirstTemplate ? "My Follow-up" : "",
    subject: "",
    body: "",
    contentType: TemplateContentType.FOLLOWUP,
  });
  const [fetchTemplateLoading, setFetchTemplateLoading] = useState(
    Boolean(templateId),
  );
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [openIsDirtyWarning, setOpenIsDirtyWarning] = useState(false);
  const [openPresaveDialog, setOpenPresaveDialog] = useState<boolean>(false);
  const hasMergeTag = containsMergeTagsTemplates(editTemplate.body || "");

  const fetchTemplate = async () => {
    if (!currentOrg?.id || !templateId) {
      return;
    }
    setFetchTemplateLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/${templateId}`,
      );
      setEditTemplate(res.template);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setFetchTemplateLoading(false);
    }
  };

  const updateReviewedFirstTemplate = async () => {
    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/templates/reviewed-first-template`,
      "POST",
      {},
    );
    setProfile((prev) => ({ ...prev, reviewedFirstTemplate: true }));
  };

  const createNewForNiche = async () => {
    if (!associatedNicheId) return;
    trackEvent("Create New Template for Niche", {
      "Template Id": templateId,
      "Niche Id": associatedNicheId,
    });
    setIsDirty(false);
    setCreateLoading(true);
    try {
      const { template } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates`,
        "POST",
        {},
        {
          ...editTemplate,
          nicheId: associatedNicheId,
          oldTemplateId: editTemplate.id,
        },
      );
      setTemplates((prev) => [...prev, template]);
      await confirmedClose();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateOrSave = () => {
    const { isValidTemplate, errorMessage } =
      validateTemplateBody(editTemplate);
    if (!isValidTemplate && errorMessage) {
      setAlert(errorMessage, "error");
      return;
    }

    if (
      templates.find(
        (t: Template) =>
          t.name === editTemplate.name &&
          t.contentType === editTemplate.contentType &&
          t.id !== editTemplate.id,
      )
    ) {
      setAlert("You already have a template with the same name", "error");
      return;
    }

    if (
      isDirty &&
      (isEdited ||
        !hasMergeTag ||
        editTemplate?.attachedScheduledEmailsCount ||
        (editTemplate?.attachedNichesCount && associatedNicheId))
    ) {
      setOpenPresaveDialog(true);
    } else {
      saveTemplate();
    }
  };

  const saveFromOnboarding = async () => {
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/templates/from-onboarding`,
        "POST",
        {},
        {
          outreach: editTemplate,
          followup: followupTemplate,
        },
      );
      await fetchOutreachTemplates();
      if (onSuccess) onSuccess();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const saveTemplate = async (updatePendingEmails?: boolean) => {
    if (!currentOrg?.id) return;
    setIsDirty(false);
    setCreateLoading(true);
    if (isUserFirstTemplate) {
      await saveFromOnboarding();
      return;
    }
    try {
      const isUpdating = Boolean(
        templateId || (editTemplate?.id && editTemplate?.id > 0),
      );
      const res = await fetcherAuth(
        getToken,
        isUpdating
          ? `/api/organization/${currentOrg?.id}/templates/${templateId || editTemplate?.id}`
          : `/api/organization/${currentOrg?.id}/templates`,
        isUpdating ? "PUT" : "POST",
        {},
        {
          ...editTemplate,
          updatePendingEmails,
          validateBulkSendVariables: isPage(routes.onboardingVaSelfServe),
        },
      );
      setEditTemplate(res.template);
      updateList(templates, setTemplates, res.template, "id");
      if (userHasNoTemplates) {
        setDefaultTemplates([res.template]);
        setUserHasNoTemplates(false);
      }
      setAlert("Template Saved", "success");
      // User add new template from QuickSend mode
      if (newlyAddedTemplateIndex > -1) {
        setTemplateForQuickSend((prev) => {
          const copy = makeDeepCopy(prev);
          copy[newlyAddedTemplateIndex] = res.template;
          handleRefetchPreviews(copy);
          return copy;
        });
      }
      if (isEdited && updatePendingEmails) {
        handleUpdateQuickSend(res.template);
      }
      confirmedClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleUpdateQuickSend = (template: Template) => {
    trackEvent("Update QuickSend Template Button Clicked", {
      "Template Id": templateId,
    });
    setTemplateForQuickSend((prev) => {
      let foundTemplate = false;
      const copy = prev.map((t) => {
        if (t.id === template.id) {
          foundTemplate = true;
          return template;
        }
        return t;
      });
      if (foundTemplate) {
        setPreviewLoading(quickSendBrandId);
        setOutreachMessages([]);
        handleRefetchPreviews(copy);
      }
      return copy;
    });
  };

  const handleClose = (e?: CustomEvent) => {
    if (isDirty) {
      setOpenIsDirtyWarning(true);
    } else {
      confirmedClose(e);
    }
  };

  const confirmedClose = async (e?: CustomEvent) => {
    if (openPresaveDialog && isPage(routes.virtualAssistant)) {
      await fetchSettings();
      loadData();
    }
    if (openPresaveDialog && isPage(routes.onboardingVaSelfServe)) {
      await fetchNiches();
    }
    setIsDirty(false);
    setOpenIsDirtyWarning(false);
    setOpenPresaveDialog(false);
    setAssociatedNicheId(null);
    if (propsHandleClose) {
      propsHandleClose(e);
    } else {
      navigate(`/${routes.templates}`);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchTemplate();
      updateReviewedFirstTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, currentOrg?.id]);

  const getDialogTitle = () => {
    if (isUserFirstTemplate) {
      return `Create your first pitch`;
    } else if (isNew) {
      return "Create a new template";
    } else {
      return "Modify your template";
    }
  };

  return (
    <StyledDialog
      title={getDialogTitle()}
      open={true}
      handleClose={handleClose}
      actionContainer={
        <>
          <Button
            disableElevation
            sx={styles.backButton}
            variant="contained"
            onClick={handleClose}
          >
            <Box
              component="i"
              className="fa-regular fa-circle-arrow-left"
              sx={{ mr: 1 }}
            />{" "}
            Back
          </Button>
          <LoadingButton
            loading={createLoading}
            disableElevation
            variant="contained"
            sx={{ px: 4, py: 1 }}
            onClick={handleCreateOrSave}
          >
            Save Template
          </LoadingButton>
        </>
      }
    >
      {isUserFirstTemplate ? (
        <UserFirstTemplate
          createLoading={createLoading}
          editTemplate={editTemplate}
          setEditTemplate={setEditTemplate}
          followupTemplate={followupTemplate}
          setFollowupTemplate={setFollowupTemplate}
          setIsDirty={setIsDirty}
        />
      ) : (
        <>
          {fetchTemplateLoading ? (
            <Grid sx={{ width: { xs: 350, md: 800 } }}>
              <Skeleton animation="wave" width="100%" height="50px" />
              <Skeleton animation="wave" width="100%" height="50px" />
              <Skeleton
                animation="wave"
                width="100%"
                height="400px"
                sx={{ mt: -10, mb: -8 }}
              />
              <Skeleton animation="wave" width="100%" height="80px" />
            </Grid>
          ) : (
            <TemplateBody
              editTemplate={editTemplate}
              setEditTemplate={setEditTemplate}
              setIsDirty={setIsDirty}
              isNew={isNew}
              allowChangeType={isNew}
              showUtilizedMergeTags={true}
            />
          )}
        </>
      )}

      <PreSaveDialog
        open={openPresaveDialog}
        handleClose={() => setOpenPresaveDialog(false)}
        template={editTemplate}
        handleConfirm={saveTemplate}
        loading={createLoading}
        createNewForNiche={createNewForNiche}
      />

      <UnsavedChanges
        isUserFirstTemplate={isUserFirstTemplate}
        confirmedClose={confirmedClose}
        setIsDirty={setIsDirty}
        setOpenIsDirtyWarning={setOpenIsDirtyWarning}
        openIsDirtyWarning={openIsDirtyWarning}
      />
    </StyledDialog>
  );
}
